import classnames from "classnames";
import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import Modal from "react-modal";
import BackButton from "web/react/components/buttons/back-button/back-button";
import CloseButton from "web/react/components/buttons/close-button/close-button";
import styles from "./overlay.css";

interface OverlayProps {
    title: string;
    onRequestClose: (
        event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
    ) => void;
    isOpen: boolean;
    contentClassName?: string;
    children: React.ReactNode;
    peek?: boolean;
    onBack?: () => void;
    onOverlayClick?: () => void;
    customClose?: JSX.Element;
}

function Overlay({
    children,
    contentClassName,
    isOpen,
    onBack,
    onOverlayClick,
    onRequestClose,
    peek = false,
    title,
    customClose,
}: OverlayProps): JSX.Element {
    let overlayRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        document.body.classList.toggle("no-scroll", isOpen);
        return (): void => {
            document.body.classList.remove("no-scroll");
        };
    }, [isOpen]);

    let appElement: HTMLElement | undefined;
    if (document.body) {
        // The modal needs to know where the rest of the site's content is for
        //  accessability reasons, and it should all be in a div called
        //  .site-wrapper as a direct decendent of the body.
        // Some things (ie cookie banner) are not in that div but we can ignore
        //  that for now
        appElement = document.body.querySelector<HTMLDivElement>(".site-wrapper") || undefined;
    }

    function handleRequestClose(event): void {
        // This check is if there is a different behaviour to close for when the users clicks the
        // overlay and close
        if (onOverlayClick && overlayRef.current && overlayRef.current.contains(event.target)) {
            onOverlayClick();
        } else {
            onRequestClose();
        }
    }

    function setOverlayRef(node): void {
        overlayRef.current = node;
    }

    return (
        <Modal
            overlayClassName={{
                base: styles.background,
                afterOpen: styles.afterOpen,
                beforeClose: styles.beforeClose,
            }}
            className={{
                base: classnames(styles.overlay, { [styles.peek]: peek }),
                afterOpen: styles.afterOpen,
                beforeClose: styles.beforeClose,
            }}
            overlayRef={setOverlayRef}
            appElement={appElement}
            onRequestClose={handleRequestClose}
            closeTimeoutMS={1000}
            isOpen={isOpen}
        >
            <div className={styles.header}>
                <span className={styles.back}>
                    <BackButton onClick={onBack ? onBack : onRequestClose} />
                </span>
                <span className={styles.title}>{title}</span>
                {customClose || (
                    <span className={styles.close}>
                        <CloseButton onClick={onRequestClose} />
                    </span>
                )}
            </div>
            <div className={classnames(styles.content, contentClassName)}>{children}</div>
        </Modal>
    );
}

export default Overlay;

Overlay.propTypes = {
    children: PropTypes.node,
    contentClassName: PropTypes.string,
    isOpen: PropTypes.bool.isRequired,
    onBack: PropTypes.func,
    onRequestClose: PropTypes.func.isRequired,
    peek: PropTypes.bool,
    title: PropTypes.string.isRequired,
};
